import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import { requireCurrentUserIdToken } from '@/features/auth/api/get-current-user-id-token';
import {
  WORKSPACE_ID_HEADER,
  readSelectedWorkspaceHeaderValue,
} from '@/features/workspaces/workspace-selection';

import { mainFirebaseApp } from './firebase-init';

export const STORYBOOK_PROXY_AUTH_BYPASS_HEADER =
  'x-minerva-storybook-auth-bypass';

type CreateProxyApiClientOptions = {
  baseURL: string;
  requireAuthBootstrap?: boolean;
  /**
   * Milliseconds before a request is abandoned. Omitted means no timeout, which
   * is axios's default and correct for uploads and long reads; a one-shot lookup
   * whose screen has no other way out should set one, or a hung socket becomes a
   * spinner with no end (ENG-2765).
   */
  timeout?: number;
};

function readHeader(
  headers: InternalAxiosRequestConfig['headers'],
  headerName: string
) {
  if (!headers) {
    return undefined;
  }

  if (typeof headers.get === 'function') {
    return headers.get(headerName) ?? headers.get(headerName.toLowerCase());
  }

  const headerRecord = headers as unknown as Record<string, string | undefined>;
  return headerRecord[headerName] ?? headerRecord[headerName.toLowerCase()];
}

function writeHeader(
  headers: InternalAxiosRequestConfig['headers'],
  headerName: string,
  value: string
) {
  if (typeof headers.set === 'function') {
    headers.set(headerName, value);
    return;
  }

  const headerRecord = headers as unknown as Record<string, string | undefined>;
  headerRecord[headerName.toLowerCase()] = value;
}

function deleteHeader(
  headers: InternalAxiosRequestConfig['headers'],
  headerName: string
) {
  if (typeof headers.delete === 'function') {
    headers.delete(headerName);
    headers.delete(headerName.toLowerCase());
    return;
  }

  const headerRecord = headers as unknown as Record<string, string | undefined>;
  delete headerRecord[headerName];
  delete headerRecord[headerName.toLowerCase()];
}

function readContentType(headers: InternalAxiosRequestConfig['headers']) {
  return readHeader(headers, 'content-type');
}

function writeContentType(
  headers: InternalAxiosRequestConfig['headers'],
  value: string
) {
  writeHeader(headers, 'content-type', value);
}

async function applyProxyRequestDefaults(
  requestConfig: InternalAxiosRequestConfig,
  requireAuthBootstrap: boolean
) {
  requestConfig.headers = requestConfig.headers ?? {};

  /**
   * Same-origin proxy requests still need to wait for the Firebase auth bridge
   * before protected hooks fire, even though the durable server session now
   * performs the backend authentication on the proxy route itself.
   */
  const shouldBypassStorybookAuthBootstrap =
    readHeader(requestConfig.headers, STORYBOOK_PROXY_AUTH_BYPASS_HEADER) ===
    'true';

  if (shouldBypassStorybookAuthBootstrap) {
    /**
     * Storybook docs seed the protected proxy responses locally, so those
     * stories do not have a real Firebase session to bridge before querying.
     * Strip the docs-only bypass marker before the request is handed to the
     * proxy adapter so it cannot leak into live application traffic.
     */
    deleteHeader(requestConfig.headers, STORYBOOK_PROXY_AUTH_BYPASS_HEADER);
  }

  if (requireAuthBootstrap && !shouldBypassStorybookAuthBootstrap) {
    await requireCurrentUserIdToken(mainFirebaseApp);
  }

  /**
   * Preserve any caller-provided content type so multipart uploads and other
   * non-JSON payloads survive the proxy hop unchanged. Only default to JSON
   * when the request did not declare a content type of its own.
   */
  const isFormDataRequest =
    typeof FormData !== 'undefined' && requestConfig.data instanceof FormData;

  if (!readContentType(requestConfig.headers) && !isFormDataRequest) {
    writeContentType(requestConfig.headers, 'application/json');
  }

  // Tenant-stamped: this interceptor has no tenant in scope, so the value it
  // reads is browser-wide and may belong to another tenant the user is signed
  // into. The stamp lets the server ignore it there instead of failing closed
  // on a selection that was never meant for this tenant.
  const selectedWorkspaceHeaderValue = readSelectedWorkspaceHeaderValue();

  if (
    selectedWorkspaceHeaderValue &&
    !readHeader(requestConfig.headers, WORKSPACE_ID_HEADER)
  ) {
    writeHeader(
      requestConfig.headers,
      WORKSPACE_ID_HEADER,
      selectedWorkspaceHeaderValue
    );
  }

  return requestConfig;
}

export function createProxyApiClient({
  baseURL,
  requireAuthBootstrap = true,
  timeout,
}: CreateProxyApiClientOptions): AxiosInstance {
  const apiClient = axios.create({
    baseURL,
    ...(timeout === undefined ? {} : { timeout }),
  });

  apiClient.interceptors.request.use((requestConfig) =>
    applyProxyRequestDefaults(requestConfig, requireAuthBootstrap)
  );

  return apiClient;
}
